import { css, StyleSheet } from 'aphrodite';
import { type FormEvent, useState } from 'react';
import { useCreateSendFileLinkMutation } from '../../../../../../../queries';
import { Button } from '../../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../../components-v2/Dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../../../../components-v2/Field';
import { Input } from '../../../../../../components-v2/Input';
import { TextArea } from '../../../../../../components-v2/TextArea';
import { SPACE } from '../../../../../../components-v2/Theme/theme';
import { userFacingErrorMessage } from '../userFacingErrorMessage';
import { FileTextIcon } from '../../../../../../components/svgs/icons/FileTextIcon';
import { MAX_DESCRIPTION_LENGTH } from '../RequestFilesDialog';
import { styleSheet as filesStyleSheet } from '../styles';

const CREATE_FAILED = 'Could not create the download link. Try again.';

const styleSheet = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
});

interface IProps {
	/** Files already saved on the contact, by attachment id. */
	attachmentIds: string[];
	contactId: string;
	/** Names of every file being sent, for the list. */
	fileNames: string[];
	/** Files picked from the computer, uploaded when the link is created. */
	newFiles: File[];
	onClose: () => void;
	onCreated: (link: string) => void;
	open: boolean;
}

export const SendFilesDialog = ({
	attachmentIds,
	contactId,
	fileNames,
	newFiles,
	onClose,
	onCreated,
	open,
}: IProps) => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [submitError, setSubmitError] = useState<string | null>(null);
	const createLink = useCreateSendFileLinkMutation();

	const reset = () => {
		setTitle('');
		setDescription('');
		setSubmitError(null);
	};

	const close = () => {
		if (createLink.isPending) {
			return;
		}
		reset();
		onClose();
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSubmitError(null);
		createLink.mutate(
			{ attachmentIds, contactId, description, files: newFiles, title },
			{
				onError: error => setSubmitError(userFacingErrorMessage(error, CREATE_FAILED)),
				onSuccess: link => {
					if (!link) {
						setSubmitError(CREATE_FAILED);
						return;
					}
					reset();
					onCreated(link);
				},
			}
		);
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && close()}>
			<DialogPopup>
				<form onSubmit={handleSubmit} noValidate>
					<DialogHeader>
						<DialogTitle>Send files</DialogTitle>
						<DialogDescription>They’ll see the title and details on the secure download page.</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.form)}>
							<div className={css(filesStyleSheet.fileRows)}>
								{fileNames.map((name, i) => (
									<div key={`${name}-${i}`} className={css(filesStyleSheet.fileRow)}>
										<FileTextIcon width={16} height={16} className={css(filesStyleSheet.fileIcon)} />
										<span className={css(filesStyleSheet.fileRowName)}>{name}</span>
									</div>
								))}
							</div>
							<Field>
								<FieldLabel htmlFor='send-files-title'>Title (optional)</FieldLabel>
								<Input
									autoFocus
									id='send-files-title'
									onChange={e => setTitle(e.target.value)}
									placeholder='e.g. Your signed trust documents'
									value={title}
								/>
							</Field>
							<Field>
								<FieldLabel htmlFor='send-files-description'>Details (optional)</FieldLabel>
								<TextArea
									id='send-files-description'
									maxLength={MAX_DESCRIPTION_LENGTH}
									onChange={e => setDescription(e.target.value)}
									placeholder='Anything they should know about these files'
									rows={4}
									value={description}
								/>
								<FieldDescription>
									{description.length}/{MAX_DESCRIPTION_LENGTH}
								</FieldDescription>
							</Field>
							{submitError ? <FieldError>{submitError}</FieldError> : null}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button disabled={createLink.isPending} onClick={close} type='button' variant='secondary'>
							Cancel
						</Button>
						<Button isLoading={createLink.isPending} type='submit'>
							Continue to email
						</Button>
					</DialogFooter>
				</form>
			</DialogPopup>
		</Dialog>
	);
};
